<template>
    <div class="filter-container">
        <h5>
            Filters
            <el-button type="text" v-if="timeRangeFilter.length > 0" @click="defaultAllFilters">Clear All</el-button>
        </h5>
        <div class="filter-list">
            <label>Upcoming / Previous</label>
            <el-select v-model="timeRangeFilter"
                multiple collapse-tags size="small"
                :class="{selected: timeRangeFilter.length, primary: true}">
                <el-option-group
                    v-for="range in rangeFilter"
                    :key="range.label"
                    :label="range.label">
                    <el-option
                        v-for="option in range.options"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                        >
                        <div class="appt">
                            <div class="appt-label">
                                <el-checkbox
                                    :value="timeRangeFilter.includes(option.value)" 
                                    style="padding-right: 5px;"></el-checkbox>
                                <span class="over-player"></span>
                                {{ option.long_lable }}
                            </div>
                            <div class="appt-count">
                                {{ totalEventMap[option.value].length }}
                            </div>
                        </div>
                    </el-option>
                </el-option-group>
            </el-select>
            <label v-if="getProviderList.length > 1">By Provider</label>
            <el-select v-model="providerFilter" 
                v-if="getProviderList.length > 1"
                placeholder="Select Provider"
                :disabled="timeRangeFilter.length === 0"
                class="secondary"
                @change="goToFilterUrl"
                multiple collapse-tags size="small">
                <el-option
                    v-for="provider in getProviderList"
                    :key="provider.provider"
                    :label="truncateText(provider.name, 12)"
                    :value="provider.provider">
                    <div class="appt">
                        <div class="appt-label">
                            <el-checkbox
                                :value="providerFilter.includes(provider.provider)" 
                                style="padding-right: 5px;"></el-checkbox>
                            <span class="over-player"></span>
                            {{ provider.name }}
                        </div>
                        <div class="appt-count">
                            {{ providerEventCount[provider.provider]?  providerEventCount[provider.provider].length : '' }}
                        </div>
                    </div>
                </el-option>
            </el-select>
            <label v-if="locationFilterList.length > 1">By Location</label>
            <el-select v-model="locationFilter"
                placeholder="Select Location"
                :disabled="timeRangeFilter.length === 0"
                v-if="locationFilterList.length > 1"
                @change="goToFilterUrl"
                class="secondary"
                multiple collapse-tags size="small">
                <el-option
                    v-for="location in locationFilterList"
                    :key="location.value"
                    :label="truncateText(location.label, 12)"
                    :value="location.value">
                    <div class="appt">
                        <div class="appt-label">
                            <el-checkbox
                                :value="locationFilter.includes(location.value)" 
                                style="padding-right: 5px;"></el-checkbox>
                            <span class="over-player"></span>
                            {{ location.label }}
                        </div>
                        <div class="appt-count">
                            {{ location.events.length }}
                        </div>
                    </div>
                </el-option>
            </el-select>
            <label v-if="eventFilterList.length > 0 || eventFilter.length == 0">By Appointment Type</label>
            <el-select v-model="eventFilter"
                placeholder="Select Appt Type"
                :disabled="timeRangeFilter.length === 0"
                class="secondary"
                @change="goToFilterUrl"
                v-if="eventFilterList.length > 0 || eventFilter.length == 0"
                multiple collapse-tags size="small">
                <el-option
                    v-for="event in eventFilterList"
                    :key="event.event"
                    :label="truncateText(event.label, 10)"
                    :value="event.event">
                    <div class="appt">
                        <div class="appt-label">
                            <el-checkbox
                                :value="eventFilter.includes(event.event)" 
                                style="padding-right: 5px;"></el-checkbox>
                            <span class="over-player"></span>
                            {{ event.label }}
                        </div>
                        <div class="appt-count">
                            {{ event.events.length }}
                        </div>
                    </div>
                </el-option>
            </el-select>
       </div>
       <div class="filter-chosen" v-if="timeRangeFilter.length > 0">
            {{ allFilterText }}
       </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import isEmptyObj from '@/module/util/empty-object'
import formatUrlSafe from '@/module/util/format-url-safe'

const previousFIlters = [
    { label: 'Past 7 Days', value: 'last_seven', long_lable: 'Appt - Past 7 Days'},
    { label: 'Past 14 Days', value: 'last_fourteen', long_lable: 'Appt - Past 14 Days'}
]
const upcomingFilters = [
    { label: 'Today', value: 'today', long_lable: 'Appt - Today'},
    { label: 'Tomorrow', value: 'tomorrow', long_lable: 'Appt - Tomorrow'},
    { label: 'Next 7 Days', value: 'next_seven', long_lable: 'Appt - Next 7 Days'}
]
export default {
    name: 'patient-filters',

    data() {
        return {
            timeRangeFilter: this.$route.query.timeRange ? this.$route.query.timeRange.split(',') : [],
            providerFilter: this.$route.query.provider ? this.$route.query.provider.split(',') : [],
            locationFilter: this.$route.query.location ? this.$route.query.location.split(',') : [],
            eventFilter: this.$route.query.event ? this.$route.query.event.split(',') : []
        }
    },

    watch: {
        timeRangeFilter: {
            handler: 'reactToChangeInTimeRangeFilter'
        },
        $route: {
            immediate: true,
            handler(to, from) {
                const { query } = to
                if(isEmptyObj(query))
                    this.defaultAllFilters()
            }
        }
    },

    computed: {
        ...mapState('provider', [
            'overview',
            'providerPatients',
        ]),
        ...mapGetters("content", ["getContentKey"]),
        ...mapGetters('provider', [
            'getProviderList',
            'getCurrentProvider'
        ]),
        /**
         * Range filter for upcoming and previous appointments
         * list with objects containing label and value
         */
        rangeFilter() {
            return [
                {
                    label: 'Upcoming',
                    options: upcomingFilters
                },
                {
                    label: 'Previous',
                    options: previousFIlters
                }
            ]
        },
        /**
         * Get a list of all locations filters by going though the overview object (`overview.schedule_map`)
         * and filtering out th locations and anything without location is left to `other` location
         */
        locationFilterList() {
            if(!this.overview || !this.overview.schedule_map) return []
            const locationFilterMap = {
                'other': []
            }
            Object.values(this.scheduleMap).forEach((eventObj) => {
                if(this.filteredEventMap.filterEverythingExceptLocation.includes(eventObj.id)) {
                    const location = eventObj.location || 'other'
                    if(!locationFilterMap[location])
                        locationFilterMap[location] = []
                    locationFilterMap[location].push(eventObj)
                }
            })
            return Object.entries(locationFilterMap).map(([location, events]) => {
                return {
                    label: location,
                    value: formatUrlSafe(location),
                    events
                }
            })
        },
        /**
         * Get a list of all event filters by going though the overview object (`overview.schedule_map`)
         * and filtering out by event id and making a list of unique events id and all events assoicated with it
         */
        eventFilterList() {
            if(!this.overview || !this.overview.schedule_map) return []
            const eventFilterMap = {}
            Object.values(this.scheduleMap).forEach(eventObj => {
                if(!this.filteredEventMap.filterEverythingExceptEvent.includes(eventObj.id))
                    return
                const eventId = eventObj.event
                // Set an empty array if the event id is not present in the map
                if(!eventFilterMap[eventId])
                    eventFilterMap[eventId] = []
                // Push the event object to the event id
                eventFilterMap[eventId].push(eventObj)
            })
            /**
             * Get the extra events which are not in the event filter list but in `eventFilter`
             */
            const extraEvents = this.eventFilter.filter(eventId => !eventFilterMap.hasOwnProperty(eventId))

            return [
                ...Object.entries(eventFilterMap).map(([eventId, events]) => {
                    return {
                        event: eventId,
                        label: this.getEventName(eventId),
                        events
                    }
                }),
                ...extraEvents.map(eventId => {
                    return {
                        event: eventId,
                        label: this.getEventName(eventId),
                        events: []
                    }
                })
            ]
        },
        /**
         * Total patient counts for type of appointments ( today, tomorrow, next_seven, last_seven, last_fourteen )
         */
        totalEventMap() {
            if(!this.overview || !this.overview.schedule)  
                return {
                    today: 0,
                    tomorrow: 0,
                    next_seven: 0,
                    last_seven: 0,
                    last_fourteen: 0
                }
            return Object.entries(this.overview.schedule).reduce((acc, [key, providerWiseObj]) => {
                acc[key] = Object.values(providerWiseObj).flat()
                return acc
            }, {})
        },
        /**
         * Filtered event list based on the time range filter calculated from the `totalEventMap`
         */
        filteredEventListOnTimeRange() {
            return Object.entries(this.totalEventMap).reduce((acc, [key, events]) => {
                if(this.timeRangeFilter.includes(key))
                    acc.push(...events)
                return  [...new Set(acc)]
            }, [])
        },
        filteredEventMap() {
            if(!this.overview || !this.overview.schedule_map) return {}
            const scheduleMap = this.scheduleMap
            // Filter everything except the event id filter (i.e. provider and location)
            let filterEverythingExceptEvent = this.filteredEventListOnTimeRange,
                filterEverythingExceptLocation = this.filteredEventListOnTimeRange,
                filterEverythingExceptProvider = this.filteredEventListOnTimeRange
            if(this.providerFilter.length > 0) {
                filterEverythingExceptEvent = this.providerFiltering(filterEverythingExceptEvent)
                filterEverythingExceptLocation = this.providerFiltering(filterEverythingExceptLocation)
            }
            if(this.locationFilter.length > 0) {
                filterEverythingExceptEvent = this.locationFiltering(filterEverythingExceptEvent)
                filterEverythingExceptProvider = this.locationFiltering(filterEverythingExceptProvider)
            }
            if(this.eventFilter.length > 0) {
                filterEverythingExceptProvider = this.eventFiltering(filterEverythingExceptProvider)
                filterEverythingExceptLocation = this.eventFiltering(filterEverythingExceptLocation)
            }

            return {
                filterEverythingExceptEvent,
                filterEverythingExceptLocation,
                filterEverythingExceptProvider
            }
        },
        /**
         * Provider wise patient count
         */
        providerEventCount() {
            if(!this.overview || !this.overview.schedule) return {}
            const providerWiseEvent = {}
            this.timeRangeFilter.forEach(filter => {
                if(this.overview.schedule[filter]) {
                    Object.entries(this.overview.schedule[filter]).forEach(([provider, events]) => {
                        if(!providerWiseEvent[provider])
                            providerWiseEvent[provider] = []
                        events.forEach(event => {
                            if(!this.filteredEventMap.filterEverythingExceptProvider.includes(event))
                                return
                            if(!providerWiseEvent[provider].includes(event))
                                providerWiseEvent[provider].push(event)
                        })
                    })
                }
            })
            return providerWiseEvent
        },
        /**
         * Text to show filter applied
         */
        allFilterText() {
            // Get the text for the time range filter
            const timeRangeText = this.timeRangeFilter.map(filter => {
                const filterObj = [...previousFIlters, ...upcomingFilters].find(f => f.value === filter)
                return filterObj ? filterObj.long_lable : ''
            }).join(', ')

            // Get the text for the provider filter
            const providerText = this.providerFilter.map(provider => {
                const providerObj = this.getProviderList.find(p => p.provider === provider)
                return providerObj ? providerObj.name : ''
            }).join(', ')

            // Get the text for the location filter
            const locationText = this.locationFilter.map(location => {
                const locationObj = this.locationFilterList.find(l => l.value === location)
                return locationObj ? locationObj.label : ''
            }).join(', ')

            // Get the text for the event filter
            const eventText = this.eventFilter.map(event => {
                const eventObj = this.eventFilterList.find(e => e.event === event)
                return eventObj ? eventObj.label : ''
            }).join(', ')

            // Return the text for all of them
            return timeRangeText + (providerText ? `, ${providerText}` : '') + (locationText ? `, ${locationText}` : '') + (eventText ? `, ${eventText}` : '')
        },
        scheduleMap() {
            return (this.overview && this.overview.schedule_map) ? this.overview.schedule_map : {}
        }
    },

    methods: {
        ...mapActions('provider', [
            'setCurrentProvider',
        ]),
        /**
         * Method to get event name from the event id
         * @param {string} eventId 
         */
        getEventName(eventId) {
            return this.getContentKey('event', eventId, 'name')
        },
        /**
         * Truncate a text to a certain length and add '...' at the end if
         * the text is longer than the specified length.
         * @param {String} text 
         * @param {Number} length 
         */
        truncateText(text, length) {
            if(!text) return ''
            if (text.length > length) {
                return text.substring(0, length) + '...'
            }
            return text
        },
        /**
         * Default value for all filters
         */
        defaultAllFilters() {
            this.timeRangeFilter = []
            this.providerFilter = []
            this.locationFilter = []
            this.eventFilter = []
        },
        /**
         * React to change in `timeRangeFilter` comparing old values and new values
         * if anything from `previousFIlters` and `upcomingFilters` is selected together,
         * in the newValue than remove all in the other category from the newValue
         * and set the newValue as the new value of `timeRangeFilter`
         * @param {Array} newValue
         * @param {Array} oldValue
         */
        reactToChangeInTimeRangeFilter(newValue, oldValue) {
            // If the newValue or oldValue is not present, return
            if(!newValue || !oldValue)
                return
            // If the newValue is empty (i.e. no appoinment date selected), return
            if(newValue.length === 0) {
                if(this.$route.path.includes('/upcoming'))
                    this.$router.push('/patient')
                return
            }
            // If there is a current provider set than set it back to null
            else if(this.getCurrentProvider)
                this.setCurrentProvider(null)
                
            
            // Get the values of the filters in a flat array
            const upcomingValues = upcomingFilters.map(f => f.value)
            const previousValues = previousFIlters.map(f => f.value)

            this.eventFilter = []
            this.providerFilter = []
            this.locationFilter = []

            // Return if no previous or upcoming values are selected together in the newValue
            if(!newValue.some(val => previousValues.includes(val)) || !newValue.some(val => upcomingValues.includes(val))) {
                this.goToFilterUrl()
                return
            }

            // Get the selected values added to the newValue
            const newValueSelected = newValue.filter(val => !oldValue.includes(val))
            // If no new value is selected, return
            if(newValueSelected.length === 0) return


            // If any of the previous filters are selected, remove all upcoming filters and vice versa
            if(newValueSelected.some(val => previousValues.includes(val))) {
                this.timeRangeFilter = newValue.filter(val => !upcomingValues.includes(val))
            }
            else if(newValueSelected.some(val => upcomingValues.includes(val)))
                this.timeRangeFilter = newValue.filter(val => !previousValues.includes(val))
            
        },
        goToFilterUrl() {
            // Set base path according to the time range filter (`forward` for upcoming and `backward` for previous)
            let path = '/upcoming/forward'
            if(this.timeRangeFilter.some(val => previousFIlters.map(f => f.value).includes(val)))
                path = '/upcoming/backward'

            // Set the query for the time range filter
            const query = {
                timeRange: this.timeRangeFilter.join(','),
            }
            // If the provider filter is not empty, add it to the query
            if(this.providerFilter.length > 0)
                query['provider'] = this.providerFilter.join(',')

            // If the location filter is not empty, add it to the query
            if(this.locationFilter.length > 0)
                query['location'] = this.locationFilter.join(',')

            // If the event filter is not empty, add it to the query
            if(this.eventFilter.length > 0)
                query['event'] = this.eventFilter.join(',')

            if(this.$route.path.includes(path) && JSON.stringify(this.$route.query) === JSON.stringify(query))
                return
            if(this.$route.path.includes(path))
                this.$router.replace({
                    path,
                    query
                })
            else
                this.$router.push({
                    path,
                    query
                })
        },
        eventFiltering(filterList) {
            return filterList.filter(eventId => {
                const eventObj = this.scheduleMap[eventId]
                return this.eventFilter.includes(eventObj.event)
            })
        },
        locationFiltering(filterList) {
            return filterList.filter(eventId => {
                const eventObj = this.scheduleMap[eventId]
                return this.locationFilter.includes(formatUrlSafe(eventObj.location || 'other'))
            })
        },
        providerFiltering(filterList) {
            return filterList.filter(eventId => {
                const eventObj = this.scheduleMap[eventId]
                if(eventObj) {
                    const provider = eventObj.provider.split(':')[0]

                    return this.providerFilter.includes(provider)
                }
                return false
            })
        }
    }

}
</script>

<style lang="scss" scoped>
.filter-container {
    .filter-list {
        padding: 0 8px;
        label {
            font-size: 11px;
            font-weight: 500;
            display: block;
            line-height: 14px;
            color: #666666;
            letter-spacing: 1px;
            margin-top: 12px;
            margin-bottom: 4px;
        }
    }
    .filter-chosen {
        background: #E2F0FF;
        font-size: 11px;
        font-weight: 600;
        color: #3572B9;
        padding: 4px 8px;
        margin-top: 12px;
        line-height: 16px;
        margin-bottom: 20px;
    }
}
.appt{
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    position: relative;
    .appt-count {
       padding-left: 12px;
    }
}
.el-select-group .el-select-dropdown__item {
    padding-left: 12px;
    padding-right: 12px;
    font-size: 13px;
}
.el-select-dropdown.is-multiple .el-select-dropdown__item {
    padding-left: 12px;
    padding-right: 12px;
}
.over-player {
    width: 25px;
    height: 25px;
    background: transparent;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
}
h5 {
    color: #999999;
    padding: 5px 10px;
    margin-top: 10px;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    .el-button {
        font-size: 11px;
        padding: 0;
        color: #DA4E4E;
        border-bottom: 1px solid;
        font-weight: 600;
    }
}
</style>

<style scoped>
.selected >>> .el-input__inner {
    border-color: #3572B9;
}
.selected >>> .el-tag.el-tag--info {
    background-color: #E2F0FF;
    border-color: #E2F0FF;
    color: #3572B9;
    font-weight: 600;
}
.selected >>> .el-tag__close.el-icon-close {
    color: #ffffff;
    background-color: #3572B9;
}
.selected >>> .el-tag__close.el-icon-close:hover {
    color: #ffffff;
    background-color: #3572B9;
}
.secondary >>> .el-input__inner[disabled] {
    background-color: transparent;
}
.secondary >>> .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #409EFF;
    border-color: #409EFF;
}
.secondary  >>> .el-tag.el-tag--info {
    background-color: #EEEEEE;
    border-color: #EEEEEE;
    color: #333333;
    font-weight: 600;
}
.secondary  >>> .el-tag__close.el-icon-close {
    color: #ffffff;
    background-color: #999999;
}
</style>

<style>
.el-select-dropdown .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #3572B9;
    border-color: #3572B9;
}
</style>